import React, { useCallback, useState } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Radio from "@mui/material/Radio";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import { styled } from "@mui/material/styles";
import { axiosClient, axiosClientCertificados } from "../AxiosClient";
import { PRAMI_COLORS } from "./certificadosTheme";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
}));

const ButtonPrincipal = styled(Button)(({ theme }) => ({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: PRAMI_COLORS.hoverMint,
    color: PRAMI_COLORS.drawerNavy,
  },
}));

const SectionTitle = styled(Typography)({
  color: PRAMI_COLORS.primary,
  fontWeight: 600,
});

const surfacePaperSx = {
  bgcolor: "#fff",
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
};

const DOCUMENT_TYPES = {
  PAZ_Y_SALVO: "PAZ_Y_SALVO",
  ESTADO_DE_CUENTA: "ESTADO_DE_CUENTA",
};

const documentTypeLabel = (type) => {
  if (type === DOCUMENT_TYPES.PAZ_Y_SALVO) return "Paz y salvo";
  if (type === DOCUMENT_TYPES.ESTADO_DE_CUENTA) return "Estado de cuenta";
  return type ?? "-";
};

/** Sugerencia informativa según el estado del crédito (no restringe la emisión). */
const suggestedDocumentType = (status) => {
  if (status === "COMPLETED") return DOCUMENT_TYPES.PAZ_Y_SALVO;
  if (status === "APPROVED") return DOCUMENT_TYPES.ESTADO_DE_CUENTA;
  return null;
};

const statusChip = (status) => {
  const map = {
    COMPLETED: { label: "Completado", color: "success" },
    APPROVED: { label: "Aprobado", color: "primary" },
  };
  const cfg = map[status];
  if (cfg) {
    return <Chip label={cfg.label} size="small" color={cfg.color} sx={{ fontWeight: 600 }} />;
  }
  return (
    <Chip
      label={status ?? "-"}
      size="small"
      variant="outlined"
      sx={{ borderColor: PRAMI_COLORS.surfaceBorder }}
    />
  );
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return "-";
  try {
    return new Date(isoStr).toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

const extractErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return fallback;
};

export default function EmisionCertificado({ onEmitido }) {
  const [documentNumber, setDocumentNumber] = useState("");

  const [validating, setValidating] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [validatedDocument, setValidatedDocument] = useState("");

  const [selectedCreditOldId, setSelectedCreditOldId] = useState(null);
  const [selectedType, setSelectedType] = useState(DOCUMENT_TYPES.PAZ_Y_SALVO);

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const resetEmissionState = () => {
    setIdentity(null);
    setSelectedCreditOldId(null);
    setSelectedType(DOCUMENT_TYPES.PAZ_Y_SALVO);
    setResult(null);
  };

  const handleValidateIdentity = () => {
    const trimmed = documentNumber.trim();
    if (!trimmed) {
      showSnackbar("Ingresa el número de cédula del cliente.", "warning");
      return;
    }
    setValidating(true);
    resetEmissionState();
    axiosClientCertificados
      .post("/v1/selfService/validate-identity", { documentNumber: trimmed })
      .then((response) => {
        const data = response.data;
        setIdentity(data);
        setValidatedDocument(trimmed);
        const credits = data?.credits || [];
        if (credits.length > 0) {
          const first = credits[0];
          setSelectedCreditOldId(first.oldId);
          setSelectedType(
            suggestedDocumentType(first.status) || DOCUMENT_TYPES.PAZ_Y_SALVO,
          );
        }
      })
      .catch((error) => {
        console.error(error);
        if (error?.response?.status === 404) {
          showSnackbar("Cliente no encontrado.", "error");
        } else {
          showSnackbar(
            extractErrorMessage(
              error,
              "Error al validar la identidad. Intenta de nuevo.",
            ),
            "error",
          );
        }
        setIdentity(null);
      })
      .finally(() => setValidating(false));
  };

  const handleSelectCredit = (credit) => {
    setSelectedCreditOldId(credit.oldId);
    const suggestion = suggestedDocumentType(credit.status);
    if (suggestion) setSelectedType(suggestion);
    setResult(null);
  };

  const handleExpedir = () => {
    if (selectedCreditOldId == null) {
      showSnackbar("Selecciona un crédito antes de expedir.", "warning");
      return;
    }
    setGenerating(true);
    setResult(null);
    axiosClient
      .post("/v1/portal/document-records/generate", {
        documentType: selectedType,
        creditOldId: selectedCreditOldId,
      })
      .then((response) => {
        const data = response.data;
        setResult(data);
        if (data?.downloadUrl) {
          window.open(data.downloadUrl, "_blank");
        }
        showSnackbar(
          `Documento expedido: ${documentTypeLabel(data?.documentType)}.`,
          "success",
        );
        if (typeof onEmitido === "function") onEmitido();
      })
      .catch((error) => {
        console.error(error);
        showSnackbar(
          extractErrorMessage(
            error,
            "Error al expedir el documento. Intenta de nuevo.",
          ),
          "error",
        );
      })
      .finally(() => setGenerating(false));
  };

  const credits = identity?.credits || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Item>
          <SectionTitle variant="h6" sx={{ mb: 0.5 }}>
            Expedir nuevo certificado
          </SectionTitle>
          <Typography variant="body2" color="text.secondary">
            Consulta la identidad del cliente, elige el crédito y expide el Paz y salvo o el Estado de cuenta.
          </Typography>
        </Item>
      </Grid>

      {/* Paso 1: validar identidad */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, ...surfacePaperSx }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <TextField
              label="Número de cédula del cliente"
              variant="outlined"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleValidateIdentity()}
              sx={{ width: 340 }}
              inputProps={{ inputMode: "numeric" }}
            />
            <ButtonPrincipal
              variant="contained"
              onClick={handleValidateIdentity}
              startIcon={validating ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              disabled={validating}
            >
              {validating ? "Consultando..." : "Consultar identidad"}
            </ButtonPrincipal>
          </Stack>

          {identity ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ mt: 2 }}
            >
              <VerifiedUserIcon sx={{ color: PRAMI_COLORS.secondaryGreen }} fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Cliente encontrado para la cédula{" "}
                <strong style={{ color: PRAMI_COLORS.primary }}>{validatedDocument}</strong>.
              </Typography>
              {identity.maskedCellphone ? (
                <Chip
                  label={`Celular: ${identity.maskedCellphone}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: PRAMI_COLORS.surfaceBorder }}
                />
              ) : null}
            </Stack>
          ) : null}
        </Paper>
      </Grid>

      {/* Paso 2: créditos y selección */}
      {identity ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 2, ...surfacePaperSx }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              sx={{ mb: 1 }}
            >
              <SectionTitle variant="subtitle1">Créditos del cliente</SectionTitle>
              <Typography variant="caption" color="text.secondary">
                La sugerencia es informativa; puedes expedir cualquiera de los dos documentos.
              </Typography>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: PRAMI_COLORS.primary }}>
                    <TableCell sx={{ color: PRAMI_COLORS.primaryContrast, fontWeight: 600 }} />
                    <TableCell sx={{ color: PRAMI_COLORS.primaryContrast, fontWeight: 600 }}>
                      ID crédito
                    </TableCell>
                    <TableCell sx={{ color: PRAMI_COLORS.primaryContrast, fontWeight: 600 }}>
                      Estado
                    </TableCell>
                    <TableCell sx={{ color: PRAMI_COLORS.primaryContrast, fontWeight: 600 }}>
                      Documento sugerido
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          El cliente no tiene créditos asociados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    credits.map((credit) => {
                      const selected = selectedCreditOldId === credit.oldId;
                      const suggestion = suggestedDocumentType(credit.status);
                      return (
                        <TableRow
                          key={credit.oldId ?? credit.uuid}
                          hover
                          selected={selected}
                          onClick={() => handleSelectCredit(credit)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Radio
                              checked={selected}
                              onChange={() => handleSelectCredit(credit)}
                              value={credit.oldId}
                              sx={{
                                color: PRAMI_COLORS.primary,
                                "&.Mui-checked": { color: PRAMI_COLORS.primary },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{credit.oldId ?? "-"}</TableCell>
                          <TableCell>{statusChip(credit.status)}</TableCell>
                          <TableCell>
                            {suggestion ? (
                              <Typography variant="body2" color="text.secondary">
                                {documentTypeLabel(suggestion)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Sin sugerencia
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ color: PRAMI_COLORS.primary }}>
                Tipo de documento a expedir
              </Typography>
              <ToggleButtonGroup
                value={selectedType}
                exclusive
                onChange={(_, value) => {
                  if (value) setSelectedType(value);
                }}
                color="primary"
              >
                <ToggleButton value={DOCUMENT_TYPES.PAZ_Y_SALVO} sx={{ textTransform: "none" }}>
                  <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                  Paz y salvo
                </ToggleButton>
                <ToggleButton value={DOCUMENT_TYPES.ESTADO_DE_CUENTA} sx={{ textTransform: "none" }}>
                  <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                  Estado de cuenta
                </ToggleButton>
              </ToggleButtonGroup>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                <ButtonPrincipal
                  variant="contained"
                  onClick={handleExpedir}
                  startIcon={
                    generating ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />
                  }
                  disabled={generating || selectedCreditOldId == null}
                >
                  {generating ? "Expidiendo..." : "Expedir"}
                </ButtonPrincipal>
                {selectedCreditOldId != null ? (
                  <Typography variant="caption" color="text.secondary">
                    Crédito{" "}
                    <strong style={{ color: PRAMI_COLORS.primary }}>{selectedCreditOldId}</strong> ·{" "}
                    {documentTypeLabel(selectedType)}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      ) : null}

      {/* Paso 3: resultado */}
      {result ? (
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              ...surfacePaperSx,
              borderColor: PRAMI_COLORS.sageSelected,
              bgcolor: PRAMI_COLORS.rowHighlight,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CheckCircleIcon sx={{ color: PRAMI_COLORS.secondaryGreen }} />
              <SectionTitle variant="subtitle1">Documento expedido correctamente</SectionTitle>
            </Stack>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Tipo de documento
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {documentTypeLabel(result.documentType)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {result.clientName ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Cédula
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {result.documentNumber ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Fecha de generación
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDateTime(result.generatedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Código de trazabilidad
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                  {result.traceabilityCode ?? "-"}
                </Typography>
              </Grid>
            </Grid>
            {result.downloadUrl ? (
              <Stack direction="row" sx={{ mt: 2 }}>
                <ButtonPrincipal
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(result.downloadUrl, "_blank")}
                >
                  Descargar PDF
                </ButtonPrincipal>
              </Stack>
            ) : null}
          </Paper>
        </Grid>
      ) : null}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
